<template>
  <div class="bg-white">
    <div v-if="fetching" class="bg-grey-light text-center py-6">
      <h3>
        <i class="fa fa-refresh fa-spin" />
        Fetching Categories...
      </h3>
    </div>
    <div v-if="!fetching && categories.length === 0" class="bg-grey-light text-center py-6">
      <h3>
        No Categories found
      </h3>
    </div>
    <div v-if="!fetching && categories.length">
      <div
        v-for="category in categories"
        :key="category.id"
        class="p-4 border-b border-solid w-full flex justify-between items-center"
      >
        <div class="flex flex-col">
          <p class="leading-normal text-black font-bold text-lg mb-2">{{ category.name }}</p>
          <p class="text-md">Item: {{ category.item_count }}</p>
        </div>

        <div class="flex items-center">
          <pt-button variant="secondary-outline" size="md" class="mr-2" @action="showAddEditCategoryModal(category)">
            <i class="fa fa-pencil mr-2" />
            Edit
          </pt-button>
          <pt-button variant="danger-outline" size="md" @action="showDeleteCategoryModal(category)">
            <i class="fa fa-trash mr-2" />
            Delete
          </pt-button>
        </div>
      </div>
      <add-edit-category-modal
        ref="addEditCategoryModal"
        :category="selectedCategory"
        :is-edit="selectedCategory !== null"
      />
      <div class="flex px-4">
        <pt-pagination
          :total-rows="categoryCount"
          :current-page="currentPage"
          :page-size="pageSize"
          :page-sizes="[10, 15, 20]"
          class="mt-8 mb-16"
          @page-changed="pageLimitHandler"
        />
      </div>
    </div>
    <delete-category-modal
      ref="deleteCategoryModal"
      :disabled="processing"
      body-text="Are you sure you want to delete this item?"
      @onConfirm="deleteCategory"
    />
  </div>
</template>

<script>
import apiRequest from '@src/utils/apiRequest';
import { RESTO_CATEGORIES, RESTO_DELETE_CATEGORY } from '@src/config/apiEndpoints';
import { PtPagination } from '@pathao/ui-components/esm/Pagination';
import { PtButton } from '@pathao/ui-components/esm/Button';
import { debounce } from 'lodash';
import AddEditCategoryModal from '@components/modals/addEditCategory.vue';
import DeleteCategoryModal from '@components/modals/deleteModal.vue';

export default {
  components: {
    PtPagination,
    PtButton,
    AddEditCategoryModal,
    DeleteCategoryModal,
  },
  props: {
    search: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      categories: [],
      fetching: false,
      categoryCount: 10,
      currentPage: 1,
      pageSize: 10,
      selectedCategory: null,
      processing: false,
    };
  },
  watch: {
    search(newSearch, oldSearch) {
      if (newSearch !== oldSearch) {
        this.debouncedGetCategories(this.currentPage, this.pageSize);
      }
    },
  },
  created() {
    this.debouncedGetCategories = debounce(this.getCategories, 500);
    this.getCategories(this.currentPage, this.pageSize);
  },
  methods: {
    hasAnyItem(category) {
      return category.item_count > 0;
    },
    getCategories(page = 1, pageSize = 10) {
      let searchParam = this.search;
      let payload = {
        url: `${RESTO_CATEGORIES}?page=${page}&page_size=${pageSize}&name=${encodeURIComponent(searchParam)}`,
        method: 'GET',
      };
      this.fetching = true;
      apiRequest(payload)
        .then(response => {
          this.categories = response.data.data;
          this.categoryCount = response.data.count;
          this.fetching = false;
        })
        .catch(() => {
          this.fetching = false;
        });
    },
    pageLimitHandler(page, pageSize) {
      this.currentPage = page;
      this.pageSize = pageSize;
      this.getCategories(page, pageSize);
    },
    showAddEditCategoryModal(category = null) {
      this.selectedCategory = category;
      this.$refs.addEditCategoryModal.show();
    },
    showDeleteCategoryModal(category = null) {
      if (this.hasAnyItem(category)) {
        this.$notification.error('Items available on this category. Please remove the items first.');
        return;
      }
      this.selectedCategory = category;
      this.$refs.deleteCategoryModal.showModal();
    },
    closeDeleteCategoryModal() {
      this.selectedCategory = null;
      this.$refs.deleteCategoryModal.closeModal();
    },
    deleteCategory() {
      this.processing = true;
      let payload = {
        url: RESTO_DELETE_CATEGORY.replace(':categoryId', this.selectedCategory.id),
        method: 'DELETE',
      };
      apiRequest(payload)
        .then(response => {
          this.processing = false;
          this.$notification.success(response.data.message);
          this.closeDeleteCategoryModal();
        })
        .catch(() => {
          this.processing = false;
          this.$notification.error('failed to delete category');
        });
    },
  },
};
</script>
